// src/pages/BlogsAll.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import BlogModal from "./BlogModal";
import "./Blogs.css";

function stripHtml(html = "") {
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || div.innerText || "").trim();
}

const LIMIT = 8;

export default function BlogsAll() {
    const [posts, setPosts] = useState([]);
    const [cats, setCats] = useState([]);
    const [catsRaw, setCatsRaw] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [openSlug, setOpenSlug] = useState(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const initialCat = searchParams.get("category") || "";
    const initialQ = searchParams.get("q") || "";
    const initialPage = Number(searchParams.get("page")) || 1;

    const [category, setCategory] = useState(initialCat);
    const [q, setQ] = useState(initialQ);
    const [qInput, setQInput] = useState(initialQ);

    useEffect(() => { setPage(initialPage); }, []); // on mount

    // URL sync
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        category ? next.set("category", category) : next.delete("category");
        q ? next.set("q", q) : next.delete("q");
        page > 1 ? next.set("page", String(page)) : next.delete("page");
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, q, page]);

    // load categories
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get("/api/blogs/categories?published=true");
                if (!cancelled) setCatsRaw(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error(e);
                if (!cancelled) setCatsRaw([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const normalizeCategory = (s = "") => s.trim().replace(/\s+/g, " ").toLowerCase();
    const toTitleCase = (s = "") =>
        s.trim().replace(/[\W_]+/g, " ")
            .split(" ").filter(Boolean)
            .map(w => w[0]?.toUpperCase() + w.slice(1).toLowerCase())
            .join(" ");

    const groupedCats = useMemo(() => {
        if (!catsRaw.length) return [];

        const map = new Map();

        for (const row of catsRaw) {
            // prefer `category`; otherwise first of legacy `categories[]`; else “uncategorised”
            const candidate =
                (typeof row.category === "string" && row.category.trim())
                    ? row.category
                    : (Array.isArray(row.categories) && row.categories.length
                        ? String(row.categories[0] ?? "").trim()
                        : "uncategorised");

            const key = normalizeCategory(candidate || "uncategorised");
            const prev = map.get(key) || { key, count: 0 };
            prev.count += 1;
            map.set(key, prev);
        }

        let out = Array.from(map.values()).map(v => ({
            key: v.key,
            label: toTitleCase(v.key),
            count: v.count,
        }));

        // Sort on the client (by count desc, then label); push 'uncategorised' last
        out.sort((a, b) => {
            if (a.key === "uncategorised" && b.key !== "uncategorised") return 1;
            if (b.key === "uncategorised" && a.key !== "uncategorised") return -1;
            return b.count - a.count || a.label.localeCompare(b.label);
        });

        // OPTIONAL: hide 'Uncategorised' if others exist
        const hasOthers = out.some(x => x.key !== "uncategorised");
        if (hasOthers) out = out.filter(x => x.key !== "uncategorised");

        return out;
    }, [catsRaw]);

    const activeKey = normalizeCategory(category);
    const groupedTotal = useMemo(
        () => groupedCats.reduce((n, c) => n + (c.count || 0), 0),
        [groupedCats]
    );

    // reset page on filter changes
    useEffect(() => { setPage(1); }, [category, q]);

    // fetch current page
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const params = new URLSearchParams({
                    published: "true",
                    limit: String(LIMIT),
                    page: String(page),
                });
                if (category) params.set("category", category); // i-case on server
                if (q) params.set("q", q);

                const { data } = await axios.get(`/api/blogs?${params.toString()}`);
                if (cancelled) return;

                const items = Array.isArray(data) ? data.slice((page - 1) * LIMIT, page * LIMIT) : (data.items || []);
                const t = Array.isArray(data) ? data.length : (data.total || 0);
                const pg = Array.isArray(data) ? Math.max(1, Math.ceil(t / LIMIT)) : (data.pages || 1);

                setPosts(items);
                setTotal(t);
                setPages(pg);
                setErr("");
            } catch (e) {
                console.error(e);
                if (!cancelled) setErr("Failed to load blogs.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [page, category, q]);

    const pageNumbers = useMemo(() => {
        const nums = [1, page - 2, page - 1, page, page + 1, page + 2, pages]
            .filter(n => n >= 1 && n <= pages);
        const uniq = [...new Set(nums)].sort((a, b) => a - b);
        const withDots = [];
        for (let i = 0; i < uniq.length; i++) {
            withDots.push(uniq[i]);
            if (i < uniq.length - 1 && uniq[i + 1] - uniq[i] > 1) withDots.push("…");
        }
        return withDots;
    }, [page, pages]);

    return (
        <div className="blog-all">
            <aside className="blog-all-sidebar">
                <h3 className="side-title">Categories</h3>
                <button
                    className={`cat-chip ${activeKey === "" ? "active" : ""}`}
                    onClick={() => setCategory("")}
                >
                    All <span className="cat-count">{groupedTotal}</span>
                </button>

                {groupedCats.map((c) => (
                    <button
                        key={c.key}
                        className={`cat-chip ${activeKey === c.key ? "active" : ""}`}
                        onClick={() => setCategory(c.label)}   // store human label; compare with normalized
                        title={c.label}
                    >
                        {c.label} <span className="cat-count">{c.count}</span>
                    </button>
                ))}

                <div className="side-divider" />
                <h3 className="side-title">Search</h3>
                <form
                    onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); }}
                    className="search-form"
                >
                    <input
                        value={qInput}
                        onChange={(e) => setQInput(e.target.value)}
                        placeholder="Search posts…"
                        className="search-input"
                    />
                    <button type="submit" className="search-btn">Search</button>
                    {q && (
                        <button
                            type="button"
                            className="clear-btn"
                            onClick={() => { setQ(""); setQInput(""); }}
                            title="Clear search"
                        >
                            ×
                        </button>
                    )}
                </form>
            </aside>

            <main className="blog-all-main">
                <header className="list-header">
                    <h1>All Blog Posts</h1>
                    <div className="list-sub">
                        Latest first · Showing {posts.length} of {total || posts.length}
                        {category ? ` in “${category}”` : ""}
                        {q ? ` · matching “${q}”` : ""}
                    </div>
                </header>

                {loading && <div className="blog-status">Loading…</div>}
                {err && !loading && <div className="blog-status error">{err}</div>}

                {!loading && !err && (
                    <>
                        <ul className="post-list">
                            {posts.map((p) => {
                                const text = stripHtml(p.body || "");
                                const preview = p.excerpt?.length ? p.excerpt : (text.length > 220 ? text.slice(0, 220) + "…" : text);
                                const date = p.publishedAt ? new Date(p.publishedAt).toLocaleDateString() : "";
                                const words = stripHtml(p.body || "").split(/\s+/).filter(Boolean).length;
                                const reading = p.readingMinutes ?? Math.max(2, Math.round(words / 220));

                                return (
                                    <li key={p._id || p.slug} className="post-row">
                                        <a
                                            className="post-thumb"
                                            href={`/blogs/${p.slug}`}
                                            onClick={(e) => { e.preventDefault(); setOpenSlug(p.slug); }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenSlug(p.slug); }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Open "${p.title}" in a modal`}
                                        >
                                            {p.heroImage ? <img src={p.heroImage} alt="" loading="lazy" /> : <div className="thumb-fallback" aria-hidden />}
                                        </a>
                                        <div className="post-body">
                                            <a
                                                className="post-title"
                                                href={`/blogs/${p.slug}`}
                                                onClick={(e) => { e.preventDefault(); setOpenSlug(p.slug); }}
                                            >
                                                {p.title}
                                            </a>
                                            <div className="post-meta">{date} · {reading} mins</div>
                                            <p className="post-excerpt">{preview}</p>
                                            <a
                                                className="post-read"
                                                href={`/blogs/${p.slug}`}
                                                onClick={(e) => { e.preventDefault(); setOpenSlug(p.slug); }}
                                            >
                                                Read more ›
                                            </a>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>

                        {pages > 1 && (
                            <nav className="hub-pagination" aria-label="Blog pagination">
                                <button className="pg-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                                    Prev
                                </button>
                                {pageNumbers.map((n, i) =>
                                    n === "…" ? (
                                        <span key={`e${i}`} className="pg-ellipsis" aria-hidden>…</span>
                                    ) : (
                                        <button
                                            key={n}
                                            className={`pg-num ${n === page ? "active" : ""}`}
                                            onClick={() => setPage(n)}
                                            aria-current={n === page ? "page" : undefined}
                                        >
                                            {n}
                                        </button>
                                    )
                                )}
                                <button className="pg-btn" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}>
                                    Next
                                </button>
                            </nav>
                        )}
                    </>
                )}
            </main>

            {openSlug && <BlogModal slug={openSlug} onClose={() => setOpenSlug(null)} />}
        </div>
    );
}
